import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { palette } from '../design-system/colors';

const useStyles = makeStyles(() => ({
    assessmentFormControlLabel: {
        padding: '12px',
        background: '#FFFFFF',
        boxShadow: '0px 4px 8px rgba(29, 30, 29, 0.06)',
        border: '2px solid rgba(#1D1E1D, 0)',
        borderRadius: '4px',
        transition: 'ease 0.2s all',

        '&:hover': {
            boxShadow:
                '0px 0px 2px rgba(0, 0, 0, 0.08), 0px 4px 24px rgba(29, 30, 29, 0.08)',
        },
        '&.active': {
            border: '0px 0px 4px rgba(29, 30, 29, 0.08)',
        },
        '&.selected': {
            border: '2px solid rgba(#1D1E1D, 0.8)',
        },
    },

    unStyledLabel: {
        boxShadow: 'none',
        padding: 0,
        marginBottom: 'unset',
        border: 'none',

        '&:hover': {
            boxShadow: 'none',
        },
        '&.selected': {
            border: 'none',
        },
    },

    incorrect: {
        border: '2px solid ' + palette.lightroom.redLight,
        backgroundColor: palette.lightroom.redLightHigh,

        '& .MuiTypography-root': {
            color: palette.vibrant.red,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-incorrect.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    somewhatCorrect: {
        border: '2px solid ' + palette.lightroom.goldLight,
        backgroundColor: palette.lightroom.goldLightHigh,

        '& .MuiTypography-root': {
            color: palette.vibrant.gold,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-somewhat-incorrect.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    correct: {
        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-correct.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    userCorrect: {
        border: '2px solid ' + palette.lightroom.aspargusLight,
        backgroundColor: palette.lightroom.aspargusLightHigh,

        '& .MuiTypography-root': {
            color: palette.evergreen.aspargus,
            fontWeight: 700,
        },
    },

    correctButIrrelevant: {
        border: '2px solid ' + palette.lightroom.heavyMetalLight,
        backgroundColor: palette.lightroom.heavyMetalLightHigh,

        '& .MuiTypography-root': {
            color: palette.graphite.heavyMetal,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-correct-but-irrelevant.svg') no-repeat center center",
            color: 'transparent',
        },
    },
}));

export const AnswerableRadioButton = observer(
    ({
        onChange,
        groupValue,
        groupId,
        value,
        text,
        answer,
        decisionKey,
        overwriteGlobalStyles,
    }) => {
        const { t } = useTranslation('assessment');
        const classes = useStyles();

        const isCorrect = () => {
            return (
                answer.correctAnswer[value] ||
                //Decision answer
                (decisionKey &&
                    answer.correctAnswer[decisionKey].value === value)
            );
        };

        const isUserCorrect = () => {
            return (
                (answer.correctAnswer[value] &&
                    answer.userAnswer.value === value) ||
                //Decision answer
                (decisionKey &&
                    answer.correctAnswer[decisionKey].value === value &&
                    answer.userAnswer[decisionKey].value === value)
            );
        };

        const isIncorrect = () => {
            return (
                (!answer.correctAnswer[value] &&
                    answer.userAnswer.value === value &&
                    answer.userAnswer.score === 0) ||
                //Decision answer
                (decisionKey &&
                    answer.correctAnswer[decisionKey].value !==
                        answer.userAnswer[decisionKey].value &&
                    answer.userAnswer[decisionKey].value === value &&
                    answer.userAnswer[decisionKey].score === 0)
            );
        };

        const isSomewhatCorrect = () => {
            return (
                !answer.correctAnswer[value] &&
                answer.userAnswer.value === value &&
                answer.userAnswer.score > 0
            );
        };

        if (!answer) {
            return (
                <FormControlLabel
                    className={classnames(classes.assessmentFormControlLabel, {
                        selected: groupValue === value,
                        [classes.unStyledLabel]: overwriteGlobalStyles,
                    })}
                    control={
                        <Radio
                            color="primary"
                            value={value}
                            onChange={(e) => onChange(e, groupId)}
                        />
                    }
                    label={text && t(text)}
                />
            );
        } else {
            return (
                <FormControlLabel
                    className={classnames(
                        'Mui-disabled',
                        classes.assessmentFormControlLabel,
                        {
                            [classes.correct]: isCorrect(),
                            [classes.userCorrect]: isUserCorrect(),
                            [classes.incorrect]: isIncorrect(),
                            [classes.somewhatCorrect]: isSomewhatCorrect(),
                            [classes.unStyledLabel]: overwriteGlobalStyles,
                        }
                    )}
                    control={<Radio color="primary" />}
                    label={text && t(text)}
                />
            );
        }
    }
);
